import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import validate from "validate.js";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col, Form } from "react-bootstrap";
import { isEmpty } from "lodash";
import moment from "moment-timezone";

import {
  addIncidentInvestigation,
  getIncidentById,
  hideIncidentValidationError,
  redirectToIncidentList,
} from "actions";

const schema = {
  event_name: {
    presence: { allowEmpty: false, message: "^Event Name is required" },
  },
};

const InvestigationTab = ({
  incidentId,
  setIncidentId,
  onNext,
  previousData,
  onBack,
  loadFromBack,
  setLoadFromBack,
}) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const incidentState = useSelector((state) => state.incidentState);
  const [eventStartDateTime, setEventStartTime] = useState(new Date());
  const [eventEndDateTime, setEventEndTime] = useState(new Date());
  const [isFinalReportGenerated, setIsFinalReportGenerated] = useState(false);
  const [incidentTimezone, setIncidentTimezone] = useState("");
  const [investigations, setInvestigations] = useState([
    {
      event_name: "",
      event_description: "",
      event_start_time: new Date(),
      event_end_time: new Date(),
    },
  ]);

  const [formState, setFormState] = useState({
    isValid: false,
    errors: [{}],
    touched: [{}],
  });

  useEffect(() => {
    if (!isEmpty(incidentState.validation_error)) {
      const errors = incidentState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [incidentState.validation_error]);

  useEffect(() => {
    if (!previousData) return;

    if (previousData.status === "final_report") {
      setIsFinalReportGenerated(true);
    }

    if (previousData.incident_timezone) {
      setIncidentTimezone(previousData.incident_timezone);
    }

    const hasEvents =
      Array.isArray(previousData.events) && previousData.events.length > 0;

    const mappedInvestigations = hasEvents
      ? previousData.events.map((event) => ({
          event_name: event.event_name || "",
          event_description: event.event_description || "",
          event_start_time: event.event_start_time
            ? new Date(event.event_start_time + "Z")
            : new Date(),
          event_end_time: event.event_end_time
            ? new Date(event.event_end_time + "Z")
            : new Date(),
        }))
      : [
          {
            event_name: "",
            event_description: "",
            event_start_time: new Date(),
            event_end_time: new Date(),
          },
        ];

    setInvestigations(mappedInvestigations);

    setFormState((prev) => ({
      ...prev,
      touched: mappedInvestigations.map(() => ({})),
      errors: mappedInvestigations.map(() => ({})),
    }));
  }, [previousData]);

  useEffect(() => {
    const newErrors = investigations.map(
      (item) => validate(item, schema) || {}
    );
    const isValid = newErrors.every((e) => Object.keys(e).length === 0);
    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
      isValid,
    }));
  }, [investigations]);

  useEffect(() => {
    if (loadFromBack && incidentId) {
      dispatch(getIncidentById(incidentId, "update"));
      setLoadFromBack(false);
    }
  }, [loadFromBack, incidentId]);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...investigations];
    updated[index][name] = value;
    setInvestigations(updated);

    const touched = [...formState.touched];
    touched[index] = { ...touched[index], [name]: true };
    setFormState((prev) => ({ ...prev, touched }));
  };

  const setDescription = (index, data) => {
    const updated = [...investigations];
    updated[index].event_description = data;
    setInvestigations(updated);

    const touched = [...formState.touched];
    touched[index] = { ...touched[index], event_description: true };
    setFormState((prev) => ({ ...prev, touched }));

    dispatch(hideIncidentValidationError("event_description"));
  };

  const handleStartTime = (index, time) => {
    const updated = [...investigations];
    updated[index].event_start_time = time;
    setInvestigations(updated);
  };

  const handleEndTime = (index, time) => {
    const updated = [...investigations];
    updated[index].event_end_time = time;
    setInvestigations(updated);
  };

  const handleAddMore = () => {
    setInvestigations([
      ...investigations,
      {
        event_name: "",
        event_description: "",
        event_start_time: new Date(),
        event_end_time: new Date(),
      },
    ]);
    setFormState((prev) => ({
      ...prev,
      errors: [...prev.errors, {}],
      touched: [...prev.touched, {}],
    }));
  };

  const handleDelete = (indexToDelete) => {
    const updated = investigations.filter((_, i) => i !== indexToDelete);
    const updatedErrors = formState.errors.filter(
      (_, i) => i !== indexToDelete
    );
    const updatedTouched = formState.touched.filter(
      (_, i) => i !== indexToDelete
    );
    setInvestigations(updated);
    setFormState((prev) => ({
      ...prev,
      errors: updatedErrors,
      touched: updatedTouched,
    }));
  };

  const hasError = (index, field) =>
    formState.touched[index]?.[field] && formState.errors[index]?.[field];

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    const formattedInvestigations = investigations.map((item, idx) => ({
      ...item,
      event_order: idx + 1, // 👈 first event = 1, second = 2, etc.
      event_start_time: item.event_start_time
        ? new Date(item.event_start_time).toISOString().slice(0, 19)
        : null,
      event_end_time: item.event_end_time
        ? new Date(item.event_end_time).toISOString().slice(0, 19)
        : null,
    }));

    const payload = {
      incident_id: incidentId,
      investigations: formattedInvestigations,
    };

    try {
      const response = await dispatch(addIncidentInvestigation(payload));
      if (response?.data?.id) {
        setIncidentId(response.data.id);
        // Refresh the main record so all tabs get the latest data
        await dispatch(getIncidentById(response.data.id, "update"));
        onNext();
      }
    } catch (error) {
      console.error("Failed to save investigation:", error);
    }
  };

  const handleSave = async (event) => {
    if (event) event.preventDefault();

     const formattedInvestigations = investigations.map((item, idx) => ({
      ...item,
      event_order: idx + 1, // 👈 first event = 1, second = 2, etc.
      event_start_time: item.event_start_time
        ? new Date(item.event_start_time).toISOString().slice(0, 19)
        : null,
      event_end_time: item.event_end_time
        ? new Date(item.event_end_time).toISOString().slice(0, 19)
        : null,
    }));
    const payload = {
      incident_id: incidentId,
      investigations: formattedInvestigations,
    };

    try {
      const response = await dispatch(addIncidentInvestigation(payload));
      // if (response?.data?.id) {
      //   setIncidentId(response.data.id);
      //   // Refresh the main record so all tabs get the latest data
      //   await dispatch(getIncidentById(response.data.id, "update"));
      //   onNext();
      // }
    } catch (error) {
      console.error("Failed to save investigation:", error);
    }
  };
  return (
    <>
      <div className="main-content-div">
        {investigations.map((item, index) => (
          <>
            {isFinalReportGenerated ? (
              <>
                {!isEmpty(item.event_name) ? (
                  <div key={item.id} className="border rounded mb-3">
                    <div className="action-content d-flex align-items-center justify-content-between">
                      <h2 className="font-13 font-weight-s-bold heading-color mb-0 d-flex align-items-center">
                        Investigation# {index + 1}: {item.event_name}
                      </h2>

                      <div className="d-flex"></div>
                    </div>
                    <div className="p-4">
                      <Row>
                        <Col lg={4}>
                          <div>
                            <h2 className="font-13 font-weight-normal heading-color">
                              Event Start Time & Date:
                            </h2>
                            <p className="font-13 font-weight-normal text-color">
                              {item.event_start_time
                                ? moment(item.event_start_time).format(
                                    "YYYY-MM-DD HH:mm"
                                  )
                                : "N/A"}
                              {` ${incidentTimezone}`}
                            </p>
                          </div>
                        </Col>

                        <Col lg={4}>
                          <div>
                            <h2 className="font-13 font-weight-normal heading-color">
                              Event End Time & Date:
                            </h2>
                            <p className="font-13 font-weight-normal text-color">
                              {item.event_end_time
                                ? moment(item.event_end_time).format(
                                    "YYYY-MM-DD HH:mm"
                                  )
                                : "N/A"}
                              {` ${incidentTimezone}`}
                            </p>
                          </div>
                        </Col>

                        <Col lg={12}>
                          <div>
                            <h2 className="font-13 font-weight-normal heading-color">
                              Event Description:
                            </h2>
                            <p
                              className="font-13 font-weight-normal text-color"
                              dangerouslySetInnerHTML={{
                                __html: item.event_description || "N/A",
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              <div
                key={index}
                className="mb-4 border rounded px-3 pb-3 pt-3 position-relative"
              >
                <Row className="align-items-start">
                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">
                        Event Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="event_name"
                        placeholder="Enter Event Name"
                        className="input-field"
                        value={item.event_name}
                        onChange={(e) => handleChange(index, e)}
                        isInvalid={hasError(index, "event_name")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {hasError(index, "event_name") &&
                          formState.errors[index].event_name[0]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">
                        Event Start Time & Date
                      </Form.Label>
                      <div className="d-flex align-items-center">
                        <DatePicker
                          selected={item.event_start_time}
                          onChange={(time) => handleStartTime(index, time)}
                          name="event_start_time"
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="yyyy-MM-dd HH:mm"
                          timeCaption="Time"
                          className="form-control input-field w-100"
                          placeholderText="Select Date & Time"
                        />
                        {incidentTimezone && (
                          <span className="ms-2 p-2 bg-light border rounded">
                            {incidentTimezone}
                          </span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">
                        Event End Time & Date
                      </Form.Label>
                      <div className="d-flex align-items-center">
                        <DatePicker
                          selected={item.event_end_time}
                          onChange={(time) => handleEndTime(index, time)}
                          name="event_end_time"
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="yyyy-MM-dd HH:mm"
                          timeCaption="Time"
                          className="form-control input-field w-100"
                          placeholderText="Select Date & Time"
                        />
                        {incidentTimezone && (
                          <span className="ms-2 p-2 bg-light border rounded">
                            {incidentTimezone}
                          </span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">
                        Event Description
                      </Form.Label>
                      <CKEditor
                        editor={ClassicEditor}
                        config={CK_CONFIGS(localStorage.getItem("token"))}
                        data={item.event_description || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          setDescription(index, data);
                        }}
                      />
                    </Form.Group>
                  </Col>

                  {investigations.length > 1 && (
                    <Col lg={12} className="text-end">
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="bg-transparent border-0"
                        title="Delete"
                      >
                        <img
                          src="/assets/images/delete-icon.svg"
                          alt="Delete"
                        />
                      </button>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </>
        ))}

        {!isFinalReportGenerated ? (
          <>
            <div className="mb-3">
              <button
                type="button"
                className="add-more-btn"
                onClick={handleAddMore}
              >
                <img src="/assets/images/add-more-icon.svg" alt="" /> Add more
              </button>
            </div>

            <div className="d-flex justify-content-start gap-3">
              <button className="c-b-btn" onClick={onBack}>
                <img className="me-2" src="/assets/images/back-arrow.svg" />{" "}
                Back
              </button>
              <button
                className="n-u-btn"
                onClick={handleSubmit}
                disabled={!formState.isValid}
              >
                Next <img className="ms-2" src="/assets/images/next-icon.svg" />
              </button>
              <button className="save-btn" onClick={handleSave}>
                Save
              </button>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>

      <div className="mt-3">
        <button
          className="c-b-btn"
          onClick={() => {
            dispatch(redirectToIncidentList());
          }}
        >
          Back To List
        </button>
      </div>
    </>
  );
};

export default InvestigationTab;
